import { useCallback, useMemo, useRef, useState } from "react"
import type { DateTime } from "luxon"

import { useDateTime } from "../../../../localization/useDateTime"
import type { Allergen } from "../../../admin/products/productsTypes"

import { AiDaySettings, bankSizeFor, pickBankDish } from "./weekMenuAiBank"
import { cellStateFor, defaultCellValues, emptyCellValues, isSameCellValues, newThemeDish } from "./weekMenuLib"
import {
    ThemeDay,
    ThemeDishValues,
    WeekCategoryRows,
    WeekDayPublishStatus,
    WeekDishRow,
    WeekMenuCell,
    WeekMenuCellState,
    WeekMenuCellValues,
    WeekMenuDayState,
    WEEK_DAY_COUNT,
} from "./weekMenuTypes"

export type CopyDishFieldKey = "title" | "description" | "allergens" | "price" | "marketingTag" | "photo"

export type WeekStore = Readonly<{
    cells: Readonly<Record<string, WeekMenuCell>>
    days: ReadonlyArray<WeekMenuDayState>
    /** When this week was last auto-saved to memory, which drives the header label. */
    lastEditedAt: DateTime | null
}>

type StoreState = Readonly<Record<string, WeekStore>>

// Enabled days must start as Published to satisfy the toggle driven status label.
const initialDayState: WeekMenuDayState = {
    isPublishEnabled: true,
    status: WeekDayPublishStatus.PUBLISHED,
    isClosed: false,
    theme: null,
}

function emptyWeekStore(): WeekStore {
    return {
        cells: {},
        days: Array.from({ length: WEEK_DAY_COUNT }, () => ({ ...initialDayState })),
        lastEditedAt: null,
    }
}

export function cellKeyFor(rowKey: string, dayIndex: number): string {
    return `${rowKey}:${dayIndex}`
}

// One cached default per row keeps DishDayCell memoized instead of re-rendering every empty cell.
const defaultCellCache = new WeakMap<WeekDishRow, WeekMenuCell>()

/** Pure read of a cell, falling back to the template defaults when untouched. */
export function cellForWeek(week: WeekStore, row: WeekDishRow, dayIndex: number): WeekMenuCell {
    const existing = week.cells[cellKeyFor(row.key, dayIndex)]
    if (existing) return existing
    let cached = defaultCellCache.get(row)
    if (!cached) {
        cached = { values: defaultCellValues(row), isTouched: false }
        defaultCellCache.set(row, cached)
    }
    return cached
}

function cellHasContent(row: WeekDishRow, cell: WeekMenuCell): boolean {
    const state = cellStateFor(row, cell.values, cell.isTouched)
    return state === WeekMenuCellState.FILLED || state === WeekMenuCellState.NEEDS_FILL
}

function asEditedDay(day: WeekMenuDayState): WeekMenuDayState {
    return day.status === WeekDayPublishStatus.PUBLISHED ? { ...day, status: WeekDayPublishStatus.UPDATED } : day
}

// Editing a published day flips its status to Updated so the kitchen sees it needs re-publishing.
function withEditedDay(days: ReadonlyArray<WeekMenuDayState>, dayIndex: number): ReadonlyArray<WeekMenuDayState> {
    return days.map((day, index) => (index === dayIndex ? asEditedDay(day) : day))
}

function withAllDaysEdited(days: ReadonlyArray<WeekMenuDayState>): ReadonlyArray<WeekMenuDayState> {
    return days.map(asEditedDay)
}

/**
 * All week-menu state lives in memory keyed by template + week — the week-menu API is not ready,
 * so this hook is the stand-in persistence layer that a future API integration will replace.
 * TODO: replace this in-memory store with useApiQuery/useApiMutation once the week menu API exists.
 *
 * The actions object is identity-stable and reads the current store inside the update callbacks:
 * a keystroke re-renders only the edited cell, not every cell of the grid.
 */
export function useWeekMenuStore(storeKey: string, rows: ReadonlyArray<WeekCategoryRows>) {
    const [stores, setStores] = useState<StoreState>({})
    /** Cells the AI review banner marks as freshly generated. */
    const [aiFilledKeys, setAiFilledKeys] = useState<ReadonlySet<string>>(new Set())
    const aiUndoRef = useRef<WeekStore | null>(null)
    const dateTimeFactory = useDateTime()
    // The stable actions read the latest template rows, week and date factory through these refs.
    const dateTimeRef = useRef(dateTimeFactory)
    dateTimeRef.current = dateTimeFactory
    const rowsRef = useRef(rows)
    rowsRef.current = rows

    const week = useMemo(() => stores[storeKey] ?? emptyWeekStore(), [stores, storeKey])
    const weekRef = useRef(week)
    weekRef.current = week

    // Returning the same store from `update` means nothing changed, so no edit is recorded.
    const updateWeek = useCallback(
        (update: (current: WeekStore) => WeekStore) => {
            setStores((current) => {
                const store = current[storeKey] ?? emptyWeekStore()
                const next = update(store)
                if (next === store) return current
                return { ...current, [storeKey]: { ...next, lastEditedAt: dateTimeRef.current.now() } }
            })
        },
        [storeKey],
    )

    const actions = useMemo(() => {
        // A null cell means the update would change nothing, so the day keeps its publish status.
        const writeCell = (rowKey: string, dayIndex: number, makeCell: (current: WeekStore) => WeekMenuCell | null) =>
            updateWeek((store) => {
                const cell = makeCell(store)
                if (cell === null) return store
                return {
                    ...store,
                    cells: { ...store.cells, [cellKeyFor(rowKey, dayIndex)]: cell },
                    days: withEditedDay(store.days, dayIndex),
                }
            })

        const updateDay = (dayIndex: number, update: (day: WeekMenuDayState) => WeekMenuDayState, isEdit = true) =>
            updateWeek((store) => ({
                ...store,
                days: store.days.map((day, index) =>
                    index === dayIndex ? (isEdit ? asEditedDay(update(day)) : update(day)) : day,
                ),
            }))

        const updateThemeOfDay = (dayIndex: number, update: (theme: ThemeDay) => ThemeDay) =>
            updateDay(dayIndex, (day) => (day.theme ? { ...day, theme: update(day.theme) } : day))

        return {
            updateCell: (row: WeekDishRow, dayIndex: number, values: Partial<WeekMenuCellValues>) =>
                writeCell(row.key, dayIndex, (store) => {
                    const current = cellForWeek(store, row, dayIndex)
                    if (isSameCellValues(current.values, values)) return null
                    return { values: { ...current.values, ...values }, isTouched: true }
                }),

            clearCell: (row: WeekDishRow, dayIndex: number) =>
                writeCell(row.key, dayIndex, () => ({ values: emptyCellValues(), isTouched: false })),

            resetCellToTemplate: (row: WeekDishRow, dayIndex: number) =>
                writeCell(row.key, dayIndex, () => ({ values: defaultCellValues(row), isTouched: false })),

            swapCells: (row: WeekDishRow, fromDay: number, toDay: number) =>
                updateWeek((store) => ({
                    ...store,
                    cells: {
                        ...store.cells,
                        [cellKeyFor(row.key, fromDay)]: cellForWeek(store, row, toDay),
                        [cellKeyFor(row.key, toDay)]: cellForWeek(store, row, fromDay),
                    },
                    days: withEditedDay(withEditedDay(store.days, fromDay), toDay),
                })),

            copyDishToDays: (
                row: WeekDishRow,
                sourceDay: number,
                targetDays: ReadonlyArray<number>,
                fields: ReadonlyArray<CopyDishFieldKey>,
                overwriteFilled: boolean,
            ) =>
                updateWeek((store) => {
                    const source = cellForWeek(store, row, sourceDay)
                    const cells = { ...store.cells }
                    let days = store.days
                    targetDays.forEach((targetDay) => {
                        const target = cellForWeek(store, row, targetDay)
                        if (cellHasContent(row, target) && !overwriteFilled) return
                        const copied = fields.reduce<WeekMenuCellValues>(
                            (values, field) => ({ ...values, [field]: source.values[field] }),
                            target.values,
                        )
                        cells[cellKeyFor(row.key, targetDay)] = { values: copied, isTouched: true }
                        days = withEditedDay(days, targetDay)
                    })
                    return { ...store, cells, days }
                }),

            clearWeek: () => {
                updateWeek((store) => ({ ...store, cells: {}, days: withAllDaysEdited(store.days) }))
                setAiFilledKeys(new Set())
            },

            /** Copies the current week's cells onto other weeks of the same template. */
            copyWeekTo: (targetStoreKeys: ReadonlyArray<string>, overwriteFilled: boolean) => {
                const allRows = rowsRef.current.flatMap((category) => category.dishes)
                const editedAt = dateTimeRef.current.now()
                setStores((current) => {
                    const source = current[storeKey] ?? emptyWeekStore()
                    const next = { ...current }
                    targetStoreKeys.forEach((targetKey) => {
                        const target = next[targetKey] ?? emptyWeekStore()
                        const cells: Record<string, WeekMenuCell> = { ...source.cells }
                        if (!overwriteFilled) {
                            // Only dishes the target week already has keep their place.
                            allRows.forEach((row) =>
                                row.availableDayIndexes.forEach((dayIndex) => {
                                    const key = cellKeyFor(row.key, dayIndex)
                                    const targetCell = target.cells[key]
                                    if (targetCell && cellHasContent(row, targetCell)) cells[key] = targetCell
                                }),
                            )
                        }
                        next[targetKey] = {
                            cells,
                            days: withAllDaysEdited(target.days),
                            lastEditedAt: editedAt,
                        }
                    })
                    return next
                })
            },

            setDayPublishEnabled: (dayIndex: number, isEnabled: boolean) =>
                updateDay(
                    dayIndex,
                    (day) => ({
                        ...day,
                        isPublishEnabled: isEnabled,
                        status: isEnabled ? WeekDayPublishStatus.PUBLISHED : WeekDayPublishStatus.DRAFT,
                    }),
                    false,
                ),

            /** Publish button: every enabled, open day becomes Published. */
            publishWeek: () =>
                updateWeek((store) => ({
                    ...store,
                    days: store.days.map((day) =>
                        day.isPublishEnabled && !day.isClosed
                            ? { ...day, status: WeekDayPublishStatus.PUBLISHED }
                            : day,
                    ),
                })),

            setDayTheme: (dayIndex: number, theme: ThemeDay | null) =>
                updateDay(dayIndex, (day) => ({ ...day, theme, isClosed: false })),

            setDayClosed: (dayIndex: number, isClosed: boolean) =>
                updateDay(dayIndex, (day) => ({ ...day, isClosed, theme: isClosed ? null : day.theme })),

            addThemeDish: (dayIndex: number) =>
                updateThemeOfDay(dayIndex, (theme) => ({ ...theme, dishes: [...theme.dishes, newThemeDish()] })),

            updateThemeDish: (dayIndex: number, dishId: string, values: Partial<ThemeDishValues>) =>
                updateThemeOfDay(dayIndex, (theme) => ({
                    ...theme,
                    dishes: theme.dishes.map((dish) =>
                        dish.id === dishId ? { ...dish, values: { ...dish.values, ...values } } : dish,
                    ),
                })),

            removeThemeDish: (dayIndex: number, dishId: string) =>
                updateThemeOfDay(dayIndex, (theme) => ({
                    ...theme,
                    dishes: theme.dishes.filter((dish) => dish.id !== dishId),
                })),

            /** AI mock: captures the undo snapshot and clears previous review marks. */
            aiBegin: () => {
                aiUndoRef.current = weekRef.current
                setAiFilledKeys(new Set())
            },

            /** Fills one day from the themed bank; the page runs the days sequentially. */
            aiFillDay: (
                dayIndex: number,
                settings: AiDaySettings,
                allergenOptions: ReadonlyArray<Allergen>,
                overwriteFilled: boolean,
            ) => {
                const current = weekRef.current
                const day = current.days[dayIndex]
                if (day.isClosed || day.theme) return
                const filledCells: Record<string, WeekMenuCell> = {}
                rowsRef.current
                    .flatMap((category) => category.dishes)
                    .forEach((row) => {
                        if (!row.availableDayIndexes.includes(dayIndex)) return
                        const key = cellKeyFor(row.key, dayIndex)
                        const cell = cellForWeek(current, row, dayIndex)
                        if (cellHasContent(row, cell) && !overwriteFilled) return
                        filledCells[key] = {
                            values: pickBankDish(row, dayIndex, settings, allergenOptions),
                            isTouched: true,
                        }
                    })
                if (Object.keys(filledCells).length === 0) return
                // An AI fill counts as an edit, so a published day flips to Updated and can be re-published.
                updateWeek((store) => ({
                    ...store,
                    cells: { ...store.cells, ...filledCells },
                    days: withEditedDay(store.days, dayIndex),
                }))
                setAiFilledKeys((previous) => {
                    const next = new Set(previous)
                    Object.keys(filledCells).forEach((key) => next.add(key))
                    return next
                })
            },

            /** Picks a different bank dish for one cell with a deterministic scan, no Math.random. */
            aiRegenerateCell: (
                row: WeekDishRow,
                dayIndex: number,
                settings: AiDaySettings,
                allergenOptions: ReadonlyArray<Allergen>,
            ) => {
                const currentTitle = cellForWeek(weekRef.current, row, dayIndex).values.title
                const size = bankSizeFor(settings.theme)
                let values = pickBankDish(row, dayIndex, settings, allergenOptions)
                for (let attempt = 1; attempt <= size && values.title === currentTitle; attempt += 1) {
                    values = pickBankDish(row, dayIndex, settings, allergenOptions, dayIndex + attempt)
                }
                const key = cellKeyFor(row.key, dayIndex)
                updateWeek((store) => ({
                    ...store,
                    cells: { ...store.cells, [key]: { values, isTouched: true } },
                    days: withEditedDay(store.days, dayIndex),
                }))
                setAiFilledKeys((previous) => new Set(previous).add(key))
            },

            aiClearMarks: () => setAiFilledKeys(new Set()),

            aiUndo: () => {
                const previous = aiUndoRef.current
                if (!previous) return
                aiUndoRef.current = null
                setStores((current) => ({ ...current, [storeKey]: previous }))
                setAiFilledKeys(new Set())
            },
        }
    }, [updateWeek, storeKey])

    return { week, days: week.days, lastEditedAt: week.lastEditedAt, aiFilledKeys, actions }
}

export type WeekMenuActions = ReturnType<typeof useWeekMenuStore>["actions"]
